import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';
import { Message } from '../services/chatService/ChatService';

// Define the shape of the context data and functions
interface ImagePreviewContextType {
    isOpen: boolean;
    imageSrc: string | null;
    imageName: string | null;
    open: (src: string, name?: string, message?: Message) => void; // <-- add message param
    close: () => void;
    isClosing: boolean;
    message?: Message; // <-- add message to context
}

// Create the context with a default value.
// The default functions throw errors to ensure they are only used within a provider.
const ImagePreviewContext = createContext<ImagePreviewContextType | undefined>(undefined);

// Create the Provider component. This will wrap your application.
export const ImagePreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [imageName, setImageName] = useState<string | null>(null);
    const [isClosing, setIsClosing] = useState(false);
    const [message, setMessage] = useState<Message | undefined>(undefined); // <-- add message state

    // The 'open' function is memoized with useCallback for performance.
    const open = useCallback((src: string, name?: string, msg?: Message) => {
        setImageSrc(src);
        setImageName(name || 'image');
        setMessage(msg); // <-- set message
        setIsClosing(false);
    }, []);

    const close = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setImageSrc(null);
            setImageName(null);
            setMessage(undefined); // <-- clear message
            setIsClosing(false);
        }, 200); // Animation duration
    }, []);

    // The modal is considered 'open' whenever there is an imageSrc
    const isOpen = !!imageSrc;

    const value = { isOpen, imageSrc, imageName, open, close, isClosing, message };

    return (
        <ImagePreviewContext.Provider value={value}>
            {children}
        </ImagePreviewContext.Provider>
    );
};

// Create a custom hook for easy access to the context.
// This is cleaner than using useContext(ImagePreviewContext) in every component.
export const useImagePreview = (): ImagePreviewContextType => {
    const context = useContext(ImagePreviewContext);
    if (context === undefined) {
        throw new Error('useImagePreview must be used within an ImagePreviewProvider');
    }
    return context;
};
